import { AnyID } from '@types'
import { useAppStore } from '@store'
import { useFrame } from '@react-three/fiber'
import { useEffect, useRef, useState } from 'react'
import type { Group } from 'three'
import {
  BackSide,
  Box3,
  BoxGeometry,
  BufferGeometry,
  Group as ThreeGroup,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Vector3,
} from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils'

type Props = {
  id: AnyID
  width: number
  height: number
  thickness: number
  parentGroupRef?: React.RefObject<Group>
  forceVisible?: boolean
  /** Cambia cuando el GLB termina de cargar (p. ej. glb.uuid) para reconstruir el outline. */
  meshRevision?: string | number
  /** Contorno por cada malla del grupo (base + cojines/hijos). */
  includeAllMeshes?: boolean
}

function createOutlineMaterial(opacity: number) {
  const OUTLINE_RED = '#ff0000'
  return new MeshBasicMaterial({
    color: OUTLINE_RED,
    transparent: true,
    opacity,
    side: BackSide,
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    toneMapped: false,
  })
}

function collectMeshGeometries(source: Group, maxMeshSpan: number) {
  const scratchBox = new Box3()
  const scratchSize = new Vector3()
  const sourceWorldInverse = source.matrixWorld.clone().invert()
  const transform = new Matrix4()
  const geometries: BufferGeometry[] = []

  source.updateWorldMatrix(true, true)
  source.traverse((obj: any) => {
    if (!obj?.isMesh || !obj.geometry) return
    if (obj.userData?.__selectionOutline) return
    const mesh = obj as Mesh
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox()
    if (mesh.geometry.boundingBox && Number.isFinite(maxMeshSpan)) {
      scratchBox.copy(mesh.geometry.boundingBox)
      scratchBox.getSize(scratchSize)
      const span = Math.max(
        scratchSize.x * Math.abs(mesh.scale.x),
        scratchSize.y * Math.abs(mesh.scale.y),
        scratchSize.z * Math.abs(mesh.scale.z),
      )
      if (span > maxMeshSpan) return
    }

    const geom = mesh.geometry.clone()
    transform.multiplyMatrices(sourceWorldInverse, mesh.matrixWorld)
    geom.applyMatrix4(transform)
    geometries.push(geom)
  })

  return geometries
}

function collectMergedGeometry(source: Group, maxMeshSpan: number) {
  const geometries = collectMeshGeometries(source, maxMeshSpan)
  if (geometries.length === 0) return null
  try {
    return mergeGeometries(geometries, false)
  } finally {
    geometries.forEach((g) => g.dispose())
  }
}

function buildPerMeshOutline(
  source: Group,
  maxMeshSpan: number,
  layerConfig: Array<{ scale: number; opacity: number }>,
) {
  const geometries = collectMeshGeometries(source, maxMeshSpan)
  if (geometries.length === 0) return null

  const wrapper = new ThreeGroup()
  wrapper.name = '__selection_outline_per_mesh__'
  wrapper.userData.__selectionOutline = true
  wrapper.userData.__meshCount = geometries.length

  let vertexCount = 0
  for (const geometry of geometries) {
    vertexCount += geometry.attributes.position?.count ?? 0
    layerConfig.forEach((layer) => {
      const outlineLayer = buildOuterMerged(geometry, layer.scale, layer.opacity)
      if (outlineLayer.children.length > 0) wrapper.add(outlineLayer)
    })
    geometry.dispose()
  }

  wrapper.userData.__vertexCount = vertexCount
  return wrapper
}

function countSourceMeshes(source: Group): number {
  let count = 0
  source.traverse((obj: any) => {
    if (obj?.isMesh && obj.geometry && !obj.userData?.__selectionOutline) count += 1
  })
  return count
}

function buildBoxFallbackOutline(
  width: number,
  height: number,
  thickness: number,
  layerConfig: Array<{ scale: number; opacity: number }>,
) {
  const w = Math.max(width, 1)
  const h = Math.max(height, 1)
  const t = Math.max(thickness, 1)
  const boxGeometry = new BoxGeometry(w, h, t)
  boxGeometry.translate(w / 2, h / 2, t / 2)

  const wrapper = new ThreeGroup()
  wrapper.name = '__selection_outline_box_fallback__'
  wrapper.userData.__selectionOutline = true
  wrapper.userData.__vertexCount = -1
  layerConfig.forEach((layer) => {
    const outlineLayer = buildOuterMerged(boxGeometry, layer.scale, layer.opacity)
    if (outlineLayer.children.length > 0) wrapper.add(outlineLayer)
  })
  boxGeometry.dispose()
  return wrapper
}

function buildOuterMerged(mergedGeometry: BufferGeometry, scaleFactor: number, opacity: number) {
  const root = new ThreeGroup()
  root.name = '__selection_outline_merged__'
  root.userData.__selectionOutline = true

  const outlineMesh = new Mesh(mergedGeometry.clone(), createOutlineMaterial(opacity))
  outlineMesh.userData.__selectionOutline = true
  outlineMesh.scale.setScalar(scaleFactor)
  outlineMesh.renderOrder = 80
  outlineMesh.raycast = () => null
  root.add(outlineMesh)

  return root
}

export function SelectionOutline({
  id,
  parentGroupRef,
  width,
  height,
  thickness,
  forceVisible = false,
  meshRevision = '',
  includeAllMeshes = false,
}: Props) {
  const isSelected = useAppStore((store) => store.layers[store.currentLayer].selected.has(id))
  const shouldShowOutline = forceVisible || isSelected
  const [outlineGroup, setOutlineGroup] = useState<ThreeGroup | null>(null)
  const outlineGroupRef = useRef<ThreeGroup | null>(null)
  const lastObservedMeshCountRef = useRef(0)
  const [meshDiscoveryToken, setMeshDiscoveryToken] = useState(0)

  useFrame(() => {
    if (!includeAllMeshes || !shouldShowOutline || !parentGroupRef?.current) return
    const meshCount = countSourceMeshes(parentGroupRef.current)
    if (meshCount !== lastObservedMeshCountRef.current) {
      lastObservedMeshCountRef.current = meshCount
      setMeshDiscoveryToken((token) => token + 1)
    }
  })

  useEffect(() => {
    if (!shouldShowOutline) lastObservedMeshCountRef.current = 0
  }, [shouldShowOutline])

  useEffect(() => {
    if (!shouldShowOutline || !parentGroupRef?.current) {
      if (outlineGroupRef.current) {
        outlineGroupRef.current = null
        setOutlineGroup(null)
      }
      return
    }
    let rafId = 0
    let cancelled = false

    const source = parentGroupRef.current
    const maxDimMeters = Math.max(width, height, thickness) / 1000
    const maxMeshSpan = includeAllMeshes
      ? Number.POSITIVE_INFINITY
      : Math.max(maxDimMeters * 8, 12)
    const maxDimMm = Math.max(width, height, thickness, 1)
    const targetBorderMm = 7.5
    const baseScale = Math.min(1.12, Math.max(1.02, 1 + targetBorderMm / (maxDimMm / 2)))
    const layerConfig = [
      { scale: Math.max(1.01, baseScale * 0.8), opacity: 0.07 },
      { scale: Math.max(1.015, baseScale * 0.9), opacity: 0.12 },
      { scale: baseScale, opacity: 0.18 },
    ]
    let warmupFramesLeft = includeAllMeshes ? 120 : 36
    let lastPublishedVertexCount = -2
    let lastPublishedMeshCount = -1
    let lastSeenMeshCount = 0
    let usedFallback = false

    const publishOutline = (wrapper: ThreeGroup, vertexCount: number) => {
      if (cancelled) return
      if (
        outlineGroupRef.current?.userData.__vertexCount === vertexCount &&
        outlineGroupRef.current?.userData.__meshCount === wrapper.userData.__meshCount
      ) {
        return
      }
      wrapper.userData.__vertexCount = vertexCount
      outlineGroupRef.current = wrapper
      setOutlineGroup(wrapper)
      lastPublishedVertexCount = vertexCount
      lastPublishedMeshCount = wrapper.userData.__meshCount as number
    }

    const buildOutlineOnce = () => {
      if (cancelled) return
      source.updateWorldMatrix(true, true)
      const meshCount = countSourceMeshes(source)
      if (meshCount > lastSeenMeshCount) {
        lastSeenMeshCount = meshCount
        warmupFramesLeft = includeAllMeshes ? 120 : warmupFramesLeft
      }

      if (includeAllMeshes) {
        const perMeshOutline = buildPerMeshOutline(source, maxMeshSpan, layerConfig)
        if (perMeshOutline) {
          const vertexCount = perMeshOutline.userData.__vertexCount as number
          const geometryImproved =
            vertexCount !== lastPublishedVertexCount || meshCount !== lastPublishedMeshCount
          if (geometryImproved) {
            publishOutline(perMeshOutline, vertexCount)
            usedFallback = false
          }
        } else if (!usedFallback || meshCount > lastPublishedMeshCount) {
          usedFallback = true
          const fallback = buildBoxFallbackOutline(width, height, thickness, layerConfig)
          fallback.userData.__meshCount = meshCount
          publishOutline(fallback, -1)
        }

        const shouldKeepTrying =
          warmupFramesLeft > 0 &&
          (meshCount === 0 || meshCount > lastPublishedMeshCount)
        if (shouldKeepTrying) {
          warmupFramesLeft -= 1
          rafId = requestAnimationFrame(buildOutlineOnce)
        }
        return
      }

      const mergedGeometry = collectMergedGeometry(source, maxMeshSpan)

      if (!mergedGeometry) {
        if (!usedFallback && warmupFramesLeft <= 24) {
          usedFallback = true
          const fallback = buildBoxFallbackOutline(width, height, thickness, layerConfig)
          fallback.userData.__meshCount = 0
          publishOutline(fallback, -1)
        }
        if (warmupFramesLeft > 0) {
          warmupFramesLeft -= 1
          rafId = requestAnimationFrame(buildOutlineOnce)
        }
        return
      }

      const vertexCount = mergedGeometry.attributes.position?.count ?? 0
      const wrapper = new ThreeGroup()
      wrapper.name = '__selection_outline_wrapper__'
      wrapper.userData.__selectionOutline = true
      wrapper.userData.__meshCount = meshCount
      layerConfig.forEach((layer) => {
        const outlineLayer = buildOuterMerged(mergedGeometry, layer.scale, layer.opacity)
        if (outlineLayer.children.length > 0) wrapper.add(outlineLayer)
      })
      mergedGeometry.dispose()

      const geometryImproved =
        vertexCount !== lastPublishedVertexCount || meshCount !== lastPublishedMeshCount
      if (geometryImproved) {
        publishOutline(wrapper, vertexCount)
        usedFallback = false
      }

      // Seguir refinando mientras el GLB añade sub-mallas (rinconero recién dibujado).
      if (warmupFramesLeft > 0) {
        warmupFramesLeft -= 1
        rafId = requestAnimationFrame(buildOutlineOnce)
      }
    }

    buildOutlineOnce()

    return () => {
      cancelled = true
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [
    shouldShowOutline,
    id,
    parentGroupRef,
    width,
    height,
    thickness,
    meshRevision,
    includeAllMeshes,
    meshDiscoveryToken,
  ])

  if (!shouldShowOutline || !outlineGroup) return null
  return <primitive object={outlineGroup} />
}
